import { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { userRequest } from '../utils/requestMethods';
import { Header, BackButton } from '../components';
import { useStateContext } from '../contexts/ContextProvider';
import { formatCurrency } from '../utils/currencyUtils';
import { displaySchoolTerm } from '../utils/schoolYearUtils';
import {
  FiUser, FiBook, FiUsers, FiCalendar, FiHash, FiCheckCircle,
  FiAlertCircle, FiTag, FiCreditCard, FiDollarSign, FiClock,
  FiArrowDown, FiInfo, FiSend
} from 'react-icons/fi';
import SkeletonTable from '../components/common/SkeletonTable';

const DetailRow = ({ label, value }) => (
  <div className="flex items-center justify-between py-3 border-b border-gray-100 last:border-0">
    <p className="text-sm text-gray-500">{label}</p>
    <p className="text-sm font-semibold text-gray-800 text-right max-w-[60%]">{value ?? '—'}</p>
  </div>
);

const TYPE_META = {
  school_fee:  { label: 'School Fee',   bg: 'bg-indigo-100', text: 'text-indigo-700' },
  card_charge: { label: 'Card Charge',  bg: 'bg-purple-100', text: 'text-purple-700' },
  withdrawal:  { label: 'Withdrawal',   bg: 'bg-orange-100', text: 'text-orange-700' },
  addon:       { label: 'Extra Fee',    bg: 'bg-teal-100',   text: 'text-teal-700'   },
};

const STATUS_META = {
  successful: { bg: 'bg-green-100',  text: 'text-green-700',  icon: FiCheckCircle,  label: 'Successful' },
  failed:     { bg: 'bg-red-100',    text: 'text-red-700',    icon: FiAlertCircle,  label: 'Failed'     },
  pending:    { bg: 'bg-yellow-100', text: 'text-yellow-700', icon: FiClock,        label: 'Pending'    },
};

const TransactionDetails = ({ history }) => {
  const { school } = useStateContext();
  const schoolId = school?._id;
  const location = useLocation();
  const transactionId = location.pathname.split('/')[2];
  const [transaction, setTransaction] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (schoolId && transactionId) {
      const fetch = async () => {
        try {
          setLoading(true);
          const { data } = await userRequest.get(`/transactions/school/${schoolId}/${transactionId}`);
          setTransaction(data);
        } catch (error) {
          console.log(error);
        } finally {
          setLoading(false);
        }
      };
      fetch();
    }
  }, [schoolId, transactionId]);

  if (loading) {
    return (
      <div className="m-4 md:m-10 mt-24 p-6 md:p-10 bg-white dark:bg-secondary-dark-bg rounded-3xl">
        <BackButton history={history} />
        <Header category="Payments" title="Transaction Details" />
        <SkeletonTable rows={8} columns={2} />
      </div>
    );
  }

  if (!transaction) {
    return (
      <div className="m-4 md:m-10 mt-24 p-6 md:p-10 bg-white dark:bg-secondary-dark-bg rounded-3xl">
        <BackButton history={history} />
        <Header category="Payments" title="Transaction Details" />
        <div className="text-center py-16 text-gray-400 text-sm">Transaction not found.</div>
      </div>
    );
  }

  const type = transaction.type || transaction.metadata?.transaction_type;
  const typeMeta = TYPE_META[type] || { label: type || 'Transaction', bg: 'bg-gray-100', text: 'text-gray-700' };
  const statusMeta = STATUS_META[transaction.status] || STATUS_META.pending;
  const StatusIcon = statusMeta.icon;

  const isSchoolFee = type === 'school_fee';
  const isCardCharge = type === 'card_charge';

  const outstanding = Number(transaction.total_amount || 0) - Number(transaction.amount_after || 0);
  const isPaid = outstanding <= 0;

  const counterParty = transaction.metadata?.counterParty;
  const authorization = transaction.authorization;
  const customer = transaction.customer;

  return (
    <div className="m-4 md:m-10 mt-24 p-6 md:p-10 bg-white dark:bg-secondary-dark-bg rounded-3xl">
      <BackButton history={history} />
      <Header category="Payments" title="Transaction Details" />

      <div className="max-w-2xl mx-auto space-y-5">

        {/* Type + status row */}
        <div className="flex flex-wrap items-center gap-2">
          <span className={`text-xs font-semibold px-3 py-1 rounded-full ${typeMeta.bg} ${typeMeta.text}`}>
            {typeMeta.label}
          </span>
          <span className={`inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1 rounded-full ${statusMeta.bg} ${statusMeta.text}`}>
            <StatusIcon className="text-xs" />
            {statusMeta.label}
          </span>
          {transaction.method && (
            <span className="text-xs font-medium px-3 py-1 rounded-full bg-gray-100 text-gray-600 capitalize">
              via {transaction.method}
            </span>
          )}
          {isCardCharge && transaction.channel && (
            <span className="text-xs font-medium px-3 py-1 rounded-full bg-gray-100 text-gray-600 capitalize">
              via {transaction.channel}
            </span>
          )}
        </div>

        {/* School fee: payment status banner */}
        {isSchoolFee && (
          <div className={`flex items-center gap-3 rounded-2xl px-5 py-4 border
            ${isPaid ? 'bg-green-50 border-green-200' : 'bg-amber-50 border-amber-200'}`}
          >
            {isPaid
              ? <FiCheckCircle className="text-green-600 text-2xl shrink-0" />
              : <FiAlertCircle className="text-amber-500 text-2xl shrink-0" />
            }
            <div>
              <p className={`font-semibold text-sm ${isPaid ? 'text-green-700' : 'text-amber-700'}`}>
                {isPaid ? 'Fully Paid' : 'Payment Outstanding'}
              </p>
              <p className="text-xs text-gray-500 mt-0.5">
                {displaySchoolTerm(transaction.term)} · {transaction.session}
              </p>
            </div>
            <div className="ml-auto text-right">
              <p className="text-xs text-gray-400">Balance</p>
              <p className={`text-lg font-bold ${isPaid ? 'text-green-600' : 'text-red-500'}`}>
                {formatCurrency(outstanding)}
              </p>
            </div>
          </div>
        )}

        {/* School fee: amount summary */}
        {isSchoolFee && (
          <div className="grid grid-cols-3 gap-3">
            <div className="rounded-2xl border border-gray-200 bg-gray-50 px-4 py-4 text-center">
              <p className="text-xs text-gray-400 mb-1">Total Fees</p>
              <p className="text-base font-bold text-gray-800">{formatCurrency(transaction.total_amount)}</p>
            </div>
            <div className="rounded-2xl border border-green-100 bg-green-50 px-4 py-4 text-center">
              <p className="text-xs text-gray-400 mb-1">Total Paid</p>
              <p className="text-base font-bold text-green-700">{formatCurrency(transaction.amount_after)}</p>
            </div>
            <div className="rounded-2xl border border-indigo-100 bg-indigo-50 px-4 py-4 text-center">
              <p className="text-xs text-gray-400 mb-1">This Payment</p>
              <p className="text-base font-bold text-indigo-700">{formatCurrency(transaction.amount)}</p>
            </div>
          </div>
        )}

        {/* Non-school-fee: single amount card */}
        {!isSchoolFee && (
          <div className="grid grid-cols-2 gap-3">
            <div className="rounded-2xl border border-indigo-100 bg-indigo-50 px-4 py-4 text-center">
              <p className="text-xs text-gray-400 mb-1">Amount</p>
              <p className="text-xl font-bold text-indigo-700">{formatCurrency(transaction.amount)}</p>
            </div>
            <div className="rounded-2xl border border-gray-200 bg-gray-50 px-4 py-4 text-center">
              <p className="text-xs text-gray-400 mb-1">Final Amount</p>
              <p className="text-xl font-bold text-gray-800">{formatCurrency(transaction.final_amount)}</p>
            </div>
          </div>
        )}

        {/* School fee: people */}
        {isSchoolFee && (transaction.child || transaction.metadata?.parent_name || transaction.metadata?.class) && (
          <div className="rounded-2xl border border-gray-200 px-5 py-4">
            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">People</p>
            {transaction.child && (
              <DetailRow
                label={<span className="flex items-center gap-1.5"><FiUser className="text-gray-400" /> Student</span>}
                value={`${transaction.child.firstname} ${transaction.child.lastname}`}
              />
            )}
            {transaction.metadata?.class?.class_name && (
              <DetailRow
                label={<span className="flex items-center gap-1.5"><FiBook className="text-gray-400" /> Class</span>}
                value={transaction.metadata.class.class_name}
              />
            )}
            {transaction.metadata?.parent_name && (
              <DetailRow
                label={<span className="flex items-center gap-1.5"><FiUsers className="text-gray-400" /> Parent / Guardian</span>}
                value={transaction.metadata.parent_name}
              />
            )}
            {transaction.user?.email && (
              <DetailRow
                label={<span className="flex items-center gap-1.5"><FiInfo className="text-gray-400" /> Payer Email</span>}
                value={transaction.user.email}
              />
            )}
          </div>
        )}

        {/* School fee: payer bank info */}
        {isSchoolFee && counterParty?.payerName && (
          <div className="rounded-2xl border border-gray-200 px-5 py-4">
            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">Payer Details</p>
            <DetailRow
              label={<span className="flex items-center gap-1.5"><FiUser className="text-gray-400" /> Payer Name</span>}
              value={counterParty.payerName}
            />
            {counterParty.payerBankName && (
              <DetailRow
                label={<span className="flex items-center gap-1.5"><FiSend className="text-gray-400" /> Bank</span>}
                value={counterParty.payerBankName}
              />
            )}
            {transaction.metadata?.narration && (
              <DetailRow
                label={<span className="flex items-center gap-1.5"><FiInfo className="text-gray-400" /> Narration</span>}
                value={transaction.metadata.narration}
              />
            )}
          </div>
        )}

        {/* Card charge: card details */}
        {isCardCharge && authorization && (
          <div className="rounded-2xl border border-gray-200 px-5 py-4">
            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">Card Details</p>
            <DetailRow
              label={<span className="flex items-center gap-1.5"><FiCreditCard className="text-gray-400" /> Card</span>}
              value={`${authorization.brand?.toUpperCase() || authorization.card_type?.trim()} •••• ${authorization.last4}`}
            />
            {authorization.bank && (
              <DetailRow
                label={<span className="flex items-center gap-1.5"><FiSend className="text-gray-400" /> Issuing Bank</span>}
                value={authorization.bank}
              />
            )}
            {authorization.exp_month && authorization.exp_year && (
              <DetailRow
                label="Expiry"
                value={`${authorization.exp_month}/${authorization.exp_year}`}
              />
            )}
            {authorization.country_code && (
              <DetailRow label="Country" value={authorization.country_code} />
            )}
          </div>
        )}

        {/* Card charge: customer */}
        {isCardCharge && customer?.email && (
          <div className="rounded-2xl border border-gray-200 px-5 py-4">
            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">Customer</p>
            <DetailRow
              label={<span className="flex items-center gap-1.5"><FiUser className="text-gray-400" /> Email</span>}
              value={customer.email}
            />
            {(customer.first_name || customer.last_name) && (
              <DetailRow
                label="Name"
                value={[customer.first_name, customer.last_name].filter(Boolean).join(' ')}
              />
            )}
            {customer.customer_code && (
              <DetailRow label="Customer Code" value={<span className="font-mono text-xs">{customer.customer_code}</span>} />
            )}
          </div>
        )}

        {/* Transaction info */}
        <div className="rounded-2xl border border-gray-200 px-5 py-4">
          <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">Transaction Info</p>
          <DetailRow
            label={<span className="flex items-center gap-1.5"><FiHash className="text-gray-400" /> ID</span>}
            value={<span className="font-mono text-xs">{transaction._id}</span>}
          />
          {transaction.reference && (
            <DetailRow
              label={<span className="flex items-center gap-1.5"><FiHash className="text-gray-400" /> Reference</span>}
              value={<span className="font-mono text-xs break-all">{transaction.reference}</span>}
            />
          )}
          <DetailRow
            label={<span className="flex items-center gap-1.5"><FiCalendar className="text-gray-400" /> Date</span>}
            value={new Date(transaction.created_at || transaction.createdAt).toLocaleString('en-NG', { hour12: true })}
          />
          {(isCardCharge && transaction.paid_at) && (
            <DetailRow
              label={<span className="flex items-center gap-1.5"><FiCheckCircle className="text-gray-400" /> Paid At</span>}
              value={new Date(transaction.paid_at).toLocaleString('en-NG', { hour12: true })}
            />
          )}
          {transaction.transaction_fee && Number(transaction.transaction_fee) > 0 && (
            <DetailRow
              label={<span className="flex items-center gap-1.5"><FiDollarSign className="text-gray-400" /> Transaction Fee</span>}
              value={formatCurrency(transaction.transaction_fee)}
            />
          )}
          {transaction.currency && (
            <DetailRow label="Currency" value={transaction.currency} />
          )}
          {transaction.school?.school_name && (
            <DetailRow label="School" value={transaction.school.school_name} />
          )}
          {transaction.discount?.amount > 0 && (
            <DetailRow
              label={<span className="flex items-center gap-1.5"><FiTag className="text-gray-400" /> Discount</span>}
              value={`${formatCurrency(transaction.discount.amount)} (${transaction.discount.percentage}%)`}
            />
          )}
        </div>

        {/* Add-ons */}
        {transaction.add_ons?.length > 0 && (
          <div className="rounded-2xl border border-gray-200 px-5 py-4">
            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">Extra Fees</p>
            {transaction.add_ons.map((addon) => (
              <DetailRow
                key={addon._id || addon.add_on_name}
                label={addon.add_on_name}
                value={formatCurrency(addon.add_on_price)}
              />
            ))}
          </div>
        )}

        {/* Status history */}
        {transaction.status_history?.length > 0 && (
          <div className="rounded-2xl border border-gray-200 px-5 py-4">
            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-4">Status History</p>
            <div className="space-y-3">
              {transaction.status_history.map((entry, i) => {
                const s = STATUS_META[entry.status] || STATUS_META.pending;
                const SIcon = s.icon;
                return (
                  <div key={entry._id || i} className="flex items-center gap-3">
                    <div className={`p-1.5 rounded-full ${s.bg}`}>
                      <SIcon className={`text-xs ${s.text}`} />
                    </div>
                    <div className="flex-1">
                      <p className={`text-xs font-semibold capitalize ${s.text}`}>{s.label}</p>
                      <p className="text-xs text-gray-400">
                        {new Date(entry.timestamp).toLocaleString('en-NG', { hour12: true })}
                      </p>
                    </div>
                    {i < transaction.status_history.length - 1 && (
                      <FiArrowDown className="text-gray-300 text-xs" />
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

      </div>
    </div>
  );
};

export default TransactionDetails;
